/** @format */

import React, { useMemo } from "react"; // Added useMemo
import Customize_Reworked from "./index"; // Import the layout wrapper
import { useSelector } from "react-redux";
import { ClipboardList } from "lucide-react";
import { motion } from "framer-motion";

// --- Reusable Summary Item Component ---
const SummaryItem = ({ label, value }) => {
  // Don't render the item if the value is considered empty or explicitly excluded
  if (value === null || value === undefined || value === "-" || value === "") {
    return null;
  }
  return (
    <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-100 last:border-none">
      <dt className="text-sm font-medium text-gray-600 w-full sm:w-1/3 mb-1 sm:mb-0">
        {label}
      </dt>
      <dd className="text-sm font-semibold text-gray-900 w-full sm:w-2/3">
        {value}
      </dd>
    </div>
  );
};

// --- Helper Functions for Display Names ---
// (These can be moved to a shared utils file if used elsewhere)

function getHemTypeDisplayName(hemType) {
  if (!hemType) return "-";
  const nameMap = {
    interlude: "Banded Bar",
    "interlude 2.0": "Aluminum Bar (Interlude)",
    "accubar 2.1": "Aluminum Hem",
    illusion: "Aluminum Bar (Illusion)",
    "plain hem": "Plain Hem",
    standard: "Standard", // For Plain Hem color
  };
  // Handle wrapped variations if stored differently
  const lowerCaseHem = hemType.toLowerCase().replace("front wrapped ", "");
  return nameMap[lowerCaseHem] || hemType;
}

function getDriveTypeDisplayName(item) {
  if (!item) return "-";
  const nameMap = {
    "zero gravity": "Glide Chainless Manual",
    chain: "Chain Manual",
    motor: "Automated (Motorized)",
    chainless: "Chainless Manual", // Added just in case
  };
  return nameMap[item.toLowerCase()] || item;
}

function getValanceTypeDisplayName(valanceName) {
  if (!valanceName) return "-";
  const nameMap = {
    'sb 3" fascia': '3" Fascia',
    "sb compact curved cassette": "Compact Curved Cassette",
    'sb 4" fascia': '4" Fascia',
    "sb curved cassette": "Curved Cassette",
    "open roll": "Open Roll",
    "square cassette": "Square Cassette",
    // Add others if needed
  };
  // Handle wrapped variations if stored differently
  const lowerCaseValance = valanceName
    .toLowerCase()
    .replace(" wrapped", "")
    .replace("wrapped ", "");
  return nameMap[lowerCaseValance] || valanceName;
}

// --- Main Summary Step Component ---
const Customize_Summary = () => {
  /*===============================  VARIABLES  =================================*/
  const { productInfo = {}, optionInfo = {} } = useSelector(
    (state) => state.customize?.customizeProduct || {},
  );

  // Determine if Cellular for conditional rendering
  const isCellular = productInfo?.Type === "Simply Cellular";
  const isVerticalSheer = productInfo?.Type === "Vertical Sheer";
  // --- Prepare Summary Data using useMemo ---
  const summaryData = useMemo(() => {
    // Basic Info
    let data = [
      // { label: "Program", value: productInfo?.Program }, // Maybe too internal?
      { label: "Shade Type", value: productInfo?.Type },
      {
        label: "Size (Width x Length)",
        value: `${optionInfo?.widthWhole || "?"}" x ${optionInfo?.lengthWhole || "?"}"`,
      },
      {
        label: "Mount Type",
        value:
          optionInfo?.measure === "Tight" ? "Inside Mount" : "Outside Mount",
      },
      ...(isVerticalSheer && optionInfo?.stackType
        ? [{ label: "Stack Type", value: optionInfo.stackType }]
        : []),
    ];

    // Fabric Info
    data.push(
      {
        label: "Fabric Series",
        value: productInfo?.OCS_Name || productInfo?.Fabric,
      },
      {
        label: "Fabric Colour",
        value: productInfo?.OCS_Colour || productInfo?.SG_Color,
      },
    );

    // Non-Cellular Specifics
    if (!isCellular) {
      data.push(
        {
          label: "Control System",
          value: getDriveTypeDisplayName(productInfo?.Operation),
        },
        // Conditionally add motor options
        ...(productInfo?.Operation === "Motor"
          ? [
              { label: "Remote Added", value: productInfo?.remote },
              { label: "Charger Added", value: productInfo?.charger },
              { label: "Connector Added", value: productInfo?.connector },
            ]
          : []),
        // Conditionally add position if not Zero Gravity
        ...(productInfo?.Operation !== "Zero Gravity"
          ? [{ label: "Control Position", value: productInfo?.position }]
          : []),
        {
          label: "Roll Type",
          value:
            productInfo?.Roll_Type === "Reverse"
              ? "Reverse Roll (Fabric off front)"
              : "Regular Roll (Fabric off back)",
        }, // More descriptive
        {
          label: "Bottom Bar",
          value: getHemTypeDisplayName(productInfo?.Bottom_Bar),
        },
        {
          label: "Bottom Bar Colour",
          value: getHemTypeDisplayName(productInfo?.Bottom_Bar_Color),
        }, // Use helper in case color uses similar names
        { label: "Bottom Bar Wrapped", value: productInfo?.Hem_Wrap_Type }, // Show if applicable
        {
          label: "Valance/Cassette",
          value: getValanceTypeDisplayName(productInfo?.Valance),
        },
        { label: "Valance/Cassette Colour", value: productInfo?.Valance_Color }, // Show only if not '-'?
        // { label: "Light Guards", value: productInfo?.L_Channel } // Keep if L_Channel is relevant
      );
    } else {
      // Cellular Specifics (if any) - Example:
      // { label: "Cell Size", value: productInfo?.CellSize },
    }

    // Filter out items with no value (or specific excluded values like '-')
    return data.filter((item) => item.value && item.value !== "-");
  }, [productInfo, optionInfo, isCellular]); // Dependencies

  /*===============================  RENDER  ====================================*/
  return (
    // Wrap step content in the layout component
    <Customize_Reworked>
      <div className="space-y-6">
        {" "}
        {/* Adjusted spacing */}
        {/* --- Header --- */}
        <div className="flex items-center justify-between pb-2 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-medium">Review Your Selections</h3>
          </div>
        </div>
        <p className="text-sm text-gray-600 -mt-4">
          Please confirm your choices below before adding the shade to your
          cart. You can use the 'Back' button or the stepper above to make
          changes.
        </p>
        {/* --- Summary Card --- */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          // Removed background, border, shadow - inheriting from parent card in Customize_Reworked
          className="p-2 md:p-0" // Reduced padding, parent card has padding
        >
          {/* Description List for Semantics */}
          <dl className="divide-y divide-gray-100">
            {summaryData.map((item, index) => (
              <SummaryItem key={index} label={item.label} value={item.value} />
            ))}
            {/* Add Quantity if applicable */}
            {optionInfo?.quantity && (
              <SummaryItem label="Quantity" value={optionInfo.quantity} />
            )}
          </dl>
        </motion.div>
        {/* Final Confirmation Note */}
        <div className="text-center text-sm text-gray-500 pt-4">
          Click "Add to Cart" below to finalize this custom shade.
        </div>
      </div>
    </Customize_Reworked>
  );
};

export default Customize_Summary;
